import { useCallback, useEffect, useState } from 'react';

import { Option } from '../OptionsList/OptionsList';
import { MediaDomainEvents } from '../../../features/Media/domain/events';
import { getDevices } from '../../../features/Media/services/media';
import { useMediaContext } from '../../../features/Media/providers/MediaContextProvider';
import { publish } from '../../../features/EventsBus';

export function useAudioInputOptions(): {
	opened: boolean;
	selected?: string;
	options: Option[];
	open: () => void;
	close: () => void;
	} {
	const [options, setOptions] = useState<Option[]>([]);
	const [opened, setOpened] = useState(false);
	const [selected, setSelected] = useState<string | undefined>(undefined);

	const { selectedMicDevice } = useMediaContext();

	const loadOptions = async (): Promise<Option[]> => {
		const devices = await getDevices();

		if (devices?.audioinput.length && !selectedMicDevice) {
			setSelected(devices.audioinput[0].deviceId);
		}

		return devices?.audioinput.length
			? devices.audioinput.map((device) => {
				if (selectedMicDevice) {
					setSelected(selectedMicDevice);
				} else if (device.deviceId === 'default') {
					setSelected(device.deviceId);
				}

				return {
					key: device.deviceId,
					label: device.label,
					onClick: () => {
						onSelectedDevice(device);
					},
				};
			  })
			: [];
	};

	const onSelectedDevice = (device: MediaDeviceInfo) => {
		if (!device?.deviceId) {
			return;
		}

		publish(
			MediaDomainEvents.changeMicDevice, {
				deviceId: device.deviceId,
			}
		);
	};

	useEffect(() => {
		if (selectedMicDevice) {
			setSelected(selectedMicDevice);
		}
	}, [selectedMicDevice]);

	useEffect(() => {
		loadOptions()
			.then((devices) => {
				setOptions(devices);
			})
			.catch(() => {
				publish(MediaDomainEvents.cannotListAudioInputDevices);
			});
	}, []);

	const open = useCallback(() => {
		setOpened(true);
	}, []);

	const close = useCallback(() => {
		setOpened(false);
	}, []);

	return {
		options,
		opened,
		selected,
		open,
		close,
	};
}
